import { Chip, List } from '@hai/ui-react';
import { isEmpty, isNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BusyIcon from 'src/js/component/base/busy-icon';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import { scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import videoInputs from 'src/js/pages/streaming/video-input/video-input-store';
import { constant } from 'src/js/constant';
import { isNilOrEmpty } from 'src/js/util/global-util';

import { EssenceViewModel, essenceIsStopped, essenceStateToColor, essenceStateToIcon } from './essence-models';
import { EssenceSettings } from './essence-edit-form';
import { EssenceStatistics } from './essence-statistics';
import { startStopEssence } from './essence-store';
import { NmosServiceActiveContext } from './essence';

interface EssenceItemProps {
  model: EssenceViewModel;
  onSelect: any;
  scrollToId?: string | number;
}

export const EssenceRow = (props: EssenceItemProps) => {
  const { model, onSelect, scrollToId } = props;

  const { t } = useTranslation();
  const { sessionStore } = useStores();
  const location = useLocation();
  const showStats = location?.state?.stats === true;
  const newEssence = model.id === -1;
  const scroll = location?.state?.id === model.id || scrollToId === model.id;

  const [isExpanded, setIsExpanded] = useState<boolean>(scroll);
  const [collapseTimeoutId, setCollapseTimeoutId] = useMutable(null);
  const [expandContent, setExpandContent] = useState<string>(scroll ? (showStats ? 'STATISTICS' : 'SETTINGS') : null);
  const onExpandedStateChange = (expandedState: { eventKey: any; expanded: boolean }) => {
    if (isExpanded !== expandedState.expanded) {
      setIsExpanded(expandedState.expanded);
    }
  };

  watchEvent('expandAll:edit', () => {
    setExpandContent('SETTINGS');
    setIsExpanded(true);
  });
  watchEvent('expandAll:stats', () => {
    setExpandContent('STATISTICS');
    setIsExpanded(true);
  });
  const collapseAll = () => {
    setIsExpanded(false);
    setCollapseTimeoutId(
      setTimeout(() => {
        // the component needs to stay rendered during close for smooth closing
        setExpandContent(null);
        setCollapseTimeoutId(null);
      }, constant.collapseTime),
    );
  };
  watchEvent('collapseAll:edit', collapseAll);
  watchEvent('collapseAll:stats', collapseAll);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const onSelectHandler = (eventKey: string, expanded: boolean, ev: KeyboardEvent) => {
    switch (eventKey) {
      case 'STATISTICS':
      case 'SETTINGS':
        if (ev && ev.shiftKey) {
          // expend all
          if (expanded) {
            eventKey === 'SETTINGS' ? triggerEvent('expandAll:edit') : triggerEvent('expandAll:stats');
          } else {
            eventKey === 'SETTINGS' ? triggerEvent('collapseAll:edit') : triggerEvent('collapseAll:stats');
          }
          return;
        }
        setExpandContent(eventKey);
        if (expanded) {
          setIsExpanded(true);
          if (collapseTimeoutId() !== null) {
            clearTimeout(collapseTimeoutId());
            setCollapseTimeoutId(null); // prevent closing of panel
          }
          scroll && scrollPanelIntoView({ id: model.id });
        } else {
          setIsExpanded(false);
          setCollapseTimeoutId(
            setTimeout(() => {
              // the component needs to stay rendered during close for smooth closing
              setExpandContent(null);
              setCollapseTimeoutId(null);
            }, constant.collapseTime),
          );
        }
        break;
      case 'PLAY':
        setIsUpdating(true);
        startStopEssence(model, essenceIsStopped(model.state), false, t).then(() => {
          triggerEvent('preset-update');
          setIsUpdating(false);
        });
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    return () => {
      // final cleanup
      if (collapseTimeoutId() !== null) {
        clearTimeout(collapseTimeoutId());
      }
    };
  }, []);

  const btnId = `essence-settings-${model.id}`;
  const handleCancelEdit = () => {
    const btn = document.getElementById(btnId);
    if (!isNil(btn)) {
      btn.click();
    }
  };

  const onClickListPanel = (ev: UIEvent) => {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (
      (ev.target as any)?.className !== undefined /*
      (ev.target as any)?.className?.includes?.('HaiListPanelTitle') ||
      (ev.target as any)?.className?.includes?.('list-panel-main-content')*/
    ) {
      if (expandContent == null) {
        setExpandContent('SETTINGS');
      }
      // so we dont expand empty content
      setIsExpanded(!isExpanded);
    }
  };

  const statusColor = essenceStateToColor(model.state, model.stats.streamSync);
  const input = !isNilOrEmpty(videoInputs) && videoInputs.find((i) => i.id === model.inputChannel);
  const nmosServiceActive = React.useContext(NmosServiceActiveContext);
  return (
    <div id={`panel-${model.id}`} className={model.hidden ? 'mk-row-hidden' : ''} style={{ scrollMarginTop: '21px' }}>
      <List.Panel
        onClick={onClickListPanel}
        checked={model.selected}
        onSelect={(selected: boolean) => onSelect(model, selected)}
        panelColor={statusColor}
        disabled={newEssence || nmosServiceActive}
        expandedState={{ eventKey: expandContent, expanded: isExpanded }}
        onExpandedStateChange={onExpandedStateChange}
      >
        {!newEssence && (
          <List.PanelTitle
            onClick={onClickListPanel}
            useFsMask={true}
            title={!isEmpty(model.name) ? model.name : t('essence.settings.unnamedEssence')}
            statusIcon={essenceStateToIcon(model.state, model.stats.streamSync)}
            iconColor={statusColor}
          />
        )}
        {!newEssence && (
          <List.PanelDetail>
            <List.PanelDetailItem divider="vertical" text={t('general.id')}>
              <Chip size="sm">{model.id.toString()}</Chip>
            </List.PanelDetailItem>
            <List.PanelDetailItem divider="vertical" text={input?.name} icon="Input" />
            <List.PanelDetailItem divider="vertical" text={model.typeTxt} />
            <List.PanelDetailItem divider="vertical" text={model.addressTxt} />
            <List.PanelDetailItem text={`${t('essence.settings.payload')} ${model.rtpPayload.toString()}`} />
          </List.PanelDetail>
        )}
        {!newEssence && sessionStore.isUser() && (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
          </List.Actions>
        )}
        {!newEssence && !sessionStore.isUser() && (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
            <List.ActionItem isDivider />
            <List.ActionItem
              eventKey="PLAY"
              icon={
                <BusyIcon
                  isActive={!essenceIsStopped(model.state)}
                  activeIcon="StopFilled"
                  inactiveIcon="PlayFilled"
                  busy={isUpdating}
                />
              }
              showActive={false}
              onSelect={onSelectHandler}
              disabled={isUpdating}
            />
          </List.Actions>
        )}
        <List.ExpandedPanel>
          {expandContent === 'STATISTICS' && isExpanded && <EssenceStatistics model={model} />}
          {expandContent === 'SETTINGS' && isExpanded && (
            <EssenceSettings model={model} onCancel={handleCancelEdit} nmosServiceActive={nmosServiceActive} />
          )}
        </List.ExpandedPanel>
      </List.Panel>
    </div>
  );
};
